import { useLocalization } from "@fluent/react";
import { TableSelect, Tooltip } from "@narmi/design_system";
import { getPreviewValue } from "byzantine/src/dbbl/businessLogic/entities/csvMapping/utils";
import { PREVIEW_ROW_COUNT } from "./columnMappingConstants";
import styles from "./ColumnMappingScreen.module.scss";

export type FieldConfig<F extends string> = {
  field: F;
  labelKey: string;
  required: boolean;
  defaultNoteKey?: string | null;
  defaultNoteArgs?: Record<string, string | number>;
};

type MappingTableProps<F extends string> = {
  fields: FieldConfig<F>[];
  columnMappings: { field: F; column_index: number }[];
  columns: string[];
  previewRows: string[][];
  hasHeaderRow: boolean;
  previewRowIndex: number;
  onPreviewRowChange: (index: number) => void;
  onMappingChange: (field: F, columnIndex: number | null) => void;
  errorFields?: F[];
  warningFields?: F[];
};

const UNMAPPED_VALUE = "__unmapped__";

const MappingTable = <F extends string>({
  fields,
  columnMappings,
  columns,
  previewRows,
  hasHeaderRow,
  previewRowIndex,
  onPreviewRowChange,
  onMappingChange,
  errorFields = [],
  warningFields = [],
}: MappingTableProps<F>) => {
  const { l10n } = useLocalization();

  const mappedColumnIndices = new Set(
    columnMappings.map((cm) => cm.column_index)
  );

  const getMappingForField = (
    field: F
  ): { field: F; column_index: number } | undefined =>
    columnMappings.find((cm) => cm.field === field);

  const getColumnLabel = (index: number): string => {
    if (hasHeaderRow && columns[index]) {
      return columns[index];
    }
    return l10n.getString("column-mapping-column-label", { number: index + 1 });
  };

  const totalPreviewRows = Math.min(previewRows.length, PREVIEW_ROW_COUNT);

  return (
    <>
      <div className={styles.mappingTableScrollWrapper}>
        <div className={styles.mappingTableContainer}>
          <div className={styles.mappingTableHeader}>
            <div className={styles.mappingTableHeaderCell}>
              {l10n.getString("column-mapping-payment-fields")}
            </div>
            <div
              className={`${styles.mappingTableHeaderCell} ${styles.yourFieldsHeaderCell}`}
            >
              {l10n.getString("column-mapping-your-fields")}
            </div>
            <div className={styles.mappingTableHeaderCell}>
              <div className={styles.previewHeaderRow}>
                <div className={styles.previewHeaderLabel}>
                  <span>{l10n.getString("column-mapping-field-preview")}</span>
                  <Tooltip
                    text={l10n.getString("column-mapping-field-preview-tooltip")}
                    wrapperDisplay="flex"
                  >
                    <span className="narmi-icon-info fontColor--secondary" />
                  </Tooltip>
                </div>
                {totalPreviewRows > 1 && (
                  <div className={styles.previewPagination}>
                    <button
                      type="button"
                      className={styles.previewPaginationButton}
                      disabled={previewRowIndex === 0}
                      onClick={() => onPreviewRowChange(previewRowIndex - 1)}
                      aria-label={l10n.getString("column-mapping-previous-row")}
                    >
                      <span className="narmi-icon-chevron-left" />
                    </button>
                    <span
                      className="fontSize--xs fontColor--secondary"
                      style={{ textTransform: "none" }}
                    >
                      {l10n.getString("column-mapping-row-of", {
                        current: previewRowIndex + 1,
                        total: totalPreviewRows,
                      })}
                    </span>
                    <button
                      type="button"
                      className={styles.previewPaginationButton}
                      disabled={previewRowIndex >= totalPreviewRows - 1}
                      onClick={() => onPreviewRowChange(previewRowIndex + 1)}
                      aria-label={l10n.getString("column-mapping-next-row")}
                    >
                      <span className="narmi-icon-chevron-right" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {fields.map(({ field, labelKey, required, defaultNoteKey, defaultNoteArgs }) => {
            const mapping = getMappingForField(field);
            const selectedColumnIndex = mapping?.column_index ?? null;
            const previewValue =
              selectedColumnIndex !== null
                ? getPreviewValue(
                    previewRows,
                    selectedColumnIndex,
                    previewRowIndex
                  )
                : "";
            const selectLabel = required
              ? l10n.getString("column-mapping-select-mapping")
              : l10n.getString("column-mapping-select-mapping-optional");
            const hasError = errorFields.includes(field);
            const hasWarning = warningFields.includes(field);
            const availableColumnIndices = columns
              .map((_, colIdx) => colIdx)
              .filter(
                (colIdx) =>
                  selectedColumnIndex === colIdx ||
                  !mappedColumnIndices.has(colIdx)
              );

            return (
              <div key={field} className={styles.mappingTableRow}>
                <div
                  className={`${styles.mappingTableCell} ${styles.mappingFieldCell}`}
                >
                  <div className={styles.mappingFieldLabelGroup}>
                    <span className="fontWeight--semibold">
                      {l10n.getString(labelKey)}
                      {required && <span className="fontColor--error">*</span>}
                    </span>
                    {defaultNoteKey && (
                      <span className={styles.mappingTableDefaultNote}>
                        {l10n.getString(defaultNoteKey, defaultNoteArgs)}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.mappingTableCell}>
                  <div
                    className={`${styles.mappingTableTableSelect} ${
                      selectedColumnIndex === null
                        ? styles.mappingTableTableSelectUnmapped
                        : ""
                    } ${
                      hasWarning && !hasError
                        ? styles.mappingTableTableSelectWarning
                        : ""
                    }`}
                  >
                    <TableSelect
                      id={`column-mapping-${field}`}
                      label={selectLabel}
                      hasError={hasError}
                      value={
                        selectedColumnIndex !== null
                          ? String(selectedColumnIndex)
                          : UNMAPPED_VALUE
                      }
                      onChange={(value: string) => {
                        if (value === UNMAPPED_VALUE) {
                          onMappingChange(field, null);
                        } else {
                          const idx = parseInt(value, 10);
                          if (idx >= 0 && idx < columns.length) {
                            onMappingChange(field, idx);
                          }
                        }
                      }}
                    >
                      {[
                        <TableSelect.Item key="unmapped" value={UNMAPPED_VALUE}>
                          <span className={styles.mappingTablePlaceholder}>
                            {selectLabel}
                          </span>
                        </TableSelect.Item>,
                        ...availableColumnIndices.map((colIdx) => {
                          const colLabel = getColumnLabel(colIdx);

                          return (
                            <TableSelect.Item key={colIdx} value={String(colIdx)}>
                              <span className={styles.mappingTableOptionLabel}>
                                {colLabel}
                              </span>
                            </TableSelect.Item>
                          );
                        }),
                      ]}
                    </TableSelect>
                  </div>
                </div>
                <div
                  className={`${styles.mappingTableCell} ${styles.previewCell}`}
                >
                  <span className={previewValue ? "" : "fontColor--secondary"}>
                    {previewValue || "-"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.requiredFooter}>
        <span className="fontColor--secondary">
          {l10n.getString("column-mapping-required")}
        </span>
      </div>
    </>
  );
};

export default MappingTable;
